/**
 * La lista de ordenes.
 *
 * EL ORDEN NO ES POR FECHA DE INGRESO
 * -----------------------------------
 * Viene ordenada por fecha descendente del backend, pero lo que se ve primero
 * son los equipos ABIERTOS: un entregado o un anulado ya no es trabajo, es
 * historia, y ocupar las primeras filas con historia obliga a scrollear para
 * ver lo que hay que hacer hoy. Dentro de cada grupo si manda la antiguedad: el
 * que hace mas dias que espera va arriba.
 *
 * La columna de dias no es decorativa. Es la unica que dice si el taller esta
 * al dia, y por eso se pinta cuando el numero se pone feo.
 */
import type { KeyboardEvent, ReactNode } from "react";
import { Plus, Receipt, Wrench } from "lucide-react";

import { Importe } from "@/components/money";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Codigo } from "@/components/ui/codigo";
import { EstadoVacio } from "@/components/ui/estado-vacio";
import { SkeletonTabla, SkeletonTarjetas } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableCellImporte,
  TableHead,
  TableHeader,
  TableRow,
  TableVacia,
} from "@/components/ui/table";
import { aNumero } from "@/lib/tipos";
import { cn } from "@/lib/utils";

import {
  DIAS_SIN_RETIRAR,
  TONO_ESTADO,
  diagnosticoPendiente,
  diasEnTaller,
  esInterna,
  estaFacturada,
  etiquetaEstadoDe,
  leerPlata,
  nombreCliente,
  nombreOrden,
  ordenarParaTrabajar,
  saldoDeLaOrden,
  textoDiasDeLista,
  type OrdenTaller,
} from "./logica";

export interface ListaOrdenesProps {
  ordenes: ReadonlyArray<OrdenTaller>;
  seleccionadaId: number | null;
  onSeleccionar: (id: number) => void;
  cargando?: boolean;
  /** Que se estaba buscando, para escribir un vacio que sirva. */
  consulta: string;
  /** Cuantas ordenes hay en total, filtro aparte: distingue "taller vacio" de "filtro vacio". */
  total?: number;
  /** Con el taller vacio, el vacio ofrece recibir el primer equipo. */
  onRecibir?: () => void;
  /** Con un filtro que no encuentra nada, el vacio ofrece limpiarlo. */
  onLimpiarFiltros?: () => void;
  /** El `PieDeLista`: cuantas ordenes se trajeron de cuantas hay. */
  pie?: ReactNode;
}

const COLUMNAS = 6;

export function ListaOrdenes({
  ordenes,
  seleccionadaId,
  onSeleccionar,
  cargando = false,
  consulta,
  total,
  pie,
  onRecibir,
  onLimpiarFiltros,
}: ListaOrdenesProps): JSX.Element {
  const filas = ordenarParaTrabajar(ordenes);
  const vacio = filas.length === 0;
  // Skeleton solo en la primera carga (06 §4.2); con datos en pantalla, un
  // refetch atenua la lista y nunca la vacia.
  const primeraCarga = cargando && vacio;
  const actualizando = cargando && !vacio;
  const tallerVacio = total === 0 && !consulta.trim() && !cargando;
  const mensajeVacio = consulta.trim()
    ? `No encontré ninguna orden con «${consulta.trim()}».`
    : "No hay órdenes en esta etapa.";
  const limpiar = onLimpiarFiltros ? (
    <Button type="button" variant="outline" size="sm" onClick={onLimpiarFiltros}>
      Limpiar filtros
    </Button>
  ) : undefined;

  // Un taller sin ninguna orden no es un filtro vacio: es el primer dia. Se
  // dice eso y se ofrece lo unico que corresponde hacer.
  if (tallerVacio) {
    return (
      <EstadoVacio
        icono={Wrench}
        titulo="Todavía no entró ningún equipo"
        detalle="Cuando alguien deje un teléfono para arreglar, se carga acá con los datos del talón."
        accion={
          onRecibir ? (
            <Button type="button" onClick={onRecibir}>
              <Plus aria-hidden="true" />
              Recibir el primer equipo
            </Button>
          ) : undefined
        }
      />
    );
  }

  return (
    <section
      aria-busy={actualizando || undefined}
      className={cn("space-y-3 transition-opacity duration-150", actualizando && "opacity-60")}
    >
      {/* Desktop: tabla. La fila navega (Enter o click) y el numero es texto:
          un boton adentro de una fila clickeable es doble objetivo. */}
      <Card padding="none" className="hidden overflow-hidden md:block">
        {primeraCarga ? (
          <SkeletonTabla columnas={COLUMNAS} />
        ) : (
          <Table densidad="normal" alto="acotado">
            <TableHeader>
              <TableRow>
                <TableHead>Orden</TableHead>
                <TableHead>Equipo</TableHead>
                <TableHead>Cliente</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead className="text-right">Días</TableHead>
                <TableHead className="text-right">Precio</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {vacio ? (
                <TableVacia colSpan={COLUMNAS} mensaje={mensajeVacio} accion={limpiar} />
              ) : (
                filas.map((o) => (
                  <FilaOrden
                    key={o.id}
                    orden={o}
                    seleccionada={o.id === seleccionadaId}
                    onSeleccionar={onSeleccionar}
                  />
                ))
              )}
              {!vacio && pie ? (
                <TableRow className="hover:bg-transparent">
                  <TableCell colSpan={COLUMNAS}>{pie}</TableCell>
                </TableRow>
              ) : null}
            </TableBody>
          </Table>
        )}
      </Card>

      {/* Mobile: una tarjeta-registro por orden, tocable entera. */}
      <div className="flex flex-col gap-2 md:hidden">
        {primeraCarga ? (
          <SkeletonTarjetas />
        ) : vacio ? (
          <EstadoVacio titulo={mensajeVacio} accion={limpiar} />
        ) : (
          <>
            {filas.map((o) => (
              <TarjetaOrden
                key={o.id}
                orden={o}
                seleccionada={o.id === seleccionadaId}
                onSeleccionar={onSeleccionar}
              />
            ))}
            {pie}
          </>
        )}
      </div>
    </section>
  );
}

interface FilaProps {
  orden: OrdenTaller;
  seleccionada: boolean;
  onSeleccionar: (id: number) => void;
}

function FilaOrden({ orden: o, seleccionada, onSeleccionar }: FilaProps): JSX.Element {
  function alTeclado(e: KeyboardEvent<HTMLTableRowElement>): void {
    if (e.key !== "Enter" && e.key !== " ") return;
    e.preventDefault();
    onSeleccionar(o.id);
  }

  return (
    <TableRow
      data-state={seleccionada ? "selected" : undefined}
      tabIndex={0}
      onClick={() => onSeleccionar(o.id)}
      onKeyDown={alTeclado}
      className="cursor-pointer focus-visible:bg-accent focus-visible:outline-none"
    >
      <TableCell>
        <Codigo className="text-xs font-medium">{nombreOrden(o)}</Codigo>
      </TableCell>
      {/* Equipo y cliente con tope de ancho: el `truncate` solo no alcanza en
          una tabla, que estira la columna hasta que entra el texto entero. A
          1280px (el monitor del local) un nombre largo empujaba la columna
          Precio fuera de la tarjeta y cortaba el importe. */}
      <TableCell className="text-sm">
        <span className="block max-w-52 truncate" title={o.equipo}>
          {o.equipo}
        </span>
        {o.imei ? (
          <Codigo className="block max-w-52 truncate text-xs text-muted-foreground">
            IMEI {o.imei}
          </Codigo>
        ) : null}
      </TableCell>
      <TableCell className="text-sm">
        <span
          className={cn("block max-w-52 truncate", !o.cliente && "text-muted-foreground")}
          title={nombreCliente(o)}
        >
          {nombreCliente(o)}
        </span>
      </TableCell>
      <TableCell>
        <EstadoDeOrden orden={o} />
      </TableCell>
      <TableCell className="text-right">
        <DiasDeOrden orden={o} />
      </TableCell>
      <TableCellImporte>
        <PrecioDeOrden orden={o} />
      </TableCellImporte>
    </TableRow>
  );
}

/**
 * Tarjeta-registro (06 §3.4): titulo, linea secundaria y estado a la
 * izquierda; el importe arriba a la derecha; barra lateral con la moneda solo
 * cuando hay un precio.
 */
function TarjetaOrden({ orden: o, seleccionada, onSeleccionar }: FilaProps): JSX.Element {
  const plata = leerPlata(o);
  // <button> crudo: tarjeta-registro clickeable, toda la tarjeta es el objetivo
  // y `Card` no admite `as="button"`; el ring va adentro (`ring-inset`) para que
  // el `overflow-hidden` de la tarjeta no lo recorte.
  return (
    <Card
      as="article"
      padding="none"
      acento={plata.precio !== null ? plata.moneda : "none"}
      className="overflow-hidden"
    >
      {/* <button> crudo: tarjeta-registro clickeable entera; el ring va adentro. */}
      <button
        type="button"
        onClick={() => onSeleccionar(o.id)}
        aria-current={seleccionada ? "true" : undefined}
        className={cn(
          "flex w-full items-start justify-between gap-3 px-4 py-3 text-left transition-colors duration-150 hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring",
          seleccionada && "bg-accent",
        )}
      >
        <div className="min-w-0 space-y-1">
          <span className="block truncate text-sm font-medium">{nombreCliente(o)}</span>
          <span className="block truncate text-xs text-muted-foreground">
            {o.equipo} · <Codigo>{nombreOrden(o)}</Codigo>
          </span>
          <EstadoDeOrden orden={o} />
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1">
          <PrecioDeOrden orden={o} />
          <DiasDeOrden orden={o} />
        </div>
      </button>
    </Card>
  );
}

function EstadoDeOrden({ orden }: { orden: OrdenTaller }): JSX.Element {
  return (
    <span className="flex flex-wrap items-center gap-1.5">
      <Badge variant={TONO_ESTADO[orden.estado]}>{etiquetaEstadoDe(orden)}</Badge>
      {/* "Stock" es una etiqueta, no un estado: por eso va en outline. */}
      {esInterna(orden) ? <Badge variant="outline">Stock</Badge> : null}
      {estaFacturada(orden) ? (
        <span className="inline-flex items-center gap-1 whitespace-nowrap text-xs text-muted-foreground">
          <Receipt className="size-3" aria-hidden="true" />
          <Codigo>{orden.comprobante}</Codigo>
        </span>
      ) : null}
    </span>
  );
}

/**
 * Los dias en el taller.
 *
 * Se pintan solo cuando el equipo esta TERMINADO y sigue aca: ese es el unico
 * caso en que el numero pide una accion (llamar al cliente). Marcar en amarillo
 * una reparacion que hace ocho dias que espera un repuesto entrena a ignorar el
 * amarillo.
 */
function DiasDeOrden({ orden }: { orden: OrdenTaller }): JSX.Element {
  const dias = diasEnTaller(orden);
  const terminadoSinRetirar =
    (orden.estado === "LISTO" || orden.estado === "SIN_ARREGLO") && dias > DIAS_SIN_RETIRAR;

  return (
    <span
      className={cn(
        "whitespace-nowrap text-xs tabular-nums",
        terminadoSinRetirar ? "font-medium text-warning" : "text-muted-foreground",
      )}
    >
      {textoDiasDeLista(orden, dias)}
    </span>
  );
}

/**
 * El precio al cliente, en la moneda de los costos, con lo cobrado a cuenta
 * debajo si lo hay. Mientras el tecnico no contesto no hay numero, y se dice
 * eso en vez de mostrar un cero que parezca un precio. Lo cobrado lleva chip
 * solo cuando es el unico importe de la celda: si hay precio arriba, ese ya
 * declaro la moneda. Decia «seña», pero incluye el diagnostico cobrado al
 * recibir, que no es seña de ninguna reparacion.
 */
function PrecioDeOrden({ orden }: { orden: OrdenTaller }): JSX.Element {
  const plata = leerPlata(orden);
  // En una anulada, lo que queda cobrado: despues de «Devolver la seña» la
  // fila seguia diciendo «a cuenta» con la plata ya devuelta.
  const cobrado = orden.estado === "ANULADO" ? saldoDeLaOrden(orden).cobrado : plata.senas;
  const sena =
    cobrado > 0 ? (
      <span className="inline-flex items-baseline gap-1 whitespace-nowrap text-xs text-muted-foreground">
        a cuenta
        <Importe monto={cobrado} moneda={plata.moneda} tamano="sm" codigo={plata.precio === null} />
      </span>
    ) : null;

  // Volvio sin reparar y ya se entrego: lo que vale es lo que se facturo (el
  // diagnostico), no el precio de una reparacion que no se hizo. Se listaba
  // «ENTREGADO … US$ 120» por un equipo que solo pago US$ 20.
  if (orden.motivo_cierre && orden.estado === "ENTREGADO" && !esInterna(orden)) {
    return (
      <span className="inline-flex flex-col items-end gap-0.5">
        {estaFacturada(orden) ? (
          <Importe
            monto={aNumero(orden.facturado)}
            moneda={orden.moneda_facturado ?? plata.moneda}
            tamano="sm"
          />
        ) : (
          <span className="text-xs text-muted-foreground">sin facturar</span>
        )}
        {!estaFacturada(orden) ? sena : null}
      </span>
    );
  }

  // El diagnostico que quedo para pagar al retirar: decia «sin costos» y los
  // US$ 20 no figuraban.
  const diag = diagnosticoPendiente(orden);
  if (plata.precio === null) {
    return (
      <span className="inline-flex flex-col items-end gap-0.5">
        {diag ? (
          <>
            <span className="text-xs text-muted-foreground">diag. pendiente</span>
            <Importe monto={diag.falta} moneda={diag.moneda} tamano="sm" />
          </>
        ) : (
          <span className="text-xs text-muted-foreground">sin costos</span>
        )}
        {sena}
      </span>
    );
  }
  return (
    <span className="inline-flex flex-col items-end gap-0.5">
      <Importe monto={plata.precio} moneda={plata.moneda} tamano="sm" />
      {!plata.facturado ? sena : null}
    </span>
  );
}
